import { GRAHA_NAMES, type GrahaBody } from '@astrally/shared';
import { useI18n } from '../../i18n';

/**
 * The 120-year Vimshottari sequence as one proportional bar. Every segment's
 * width is its true share of the cycle, so Shukra's 20 years visibly dwarf
 * Surya's 6 — the point no table of numbers ever quite lands.
 *
 * Labels alternate above and below the bar because the narrow periods (Surya,
 * Ketu, Mangal) cannot carry a label side by side with their neighbours'.
 */

const PERIODS: ReadonlyArray<{ body: GrahaBody; years: number }> = [
  { body: 'ketu', years: 7 },
  { body: 'venus', years: 20 },
  { body: 'sun', years: 6 },
  { body: 'moon', years: 10 },
  { body: 'mars', years: 7 },
  { body: 'rahu', years: 18 },
  { body: 'jupiter', years: 16 },
  { body: 'saturn', years: 19 },
  { body: 'mercury', years: 17 },
];

const X0 = 2;
const WIDTH = 356;
const SCALE = WIDTH / 120;
const BAR_TOP = 56;
const BAR_H = 32;

export function DashaBar() {
  const { t } = useI18n();
  let elapsed = 0;
  return (
    <svg
      viewBox="0 0 360 150"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.dashaBar.alt')}
    >
      <text x={180} y={16} textAnchor="middle" fontSize="9.5" fill="var(--text-dim)">
        {t('learn.fig.dashaBar.total')}
      </text>

      {PERIODS.map(({ body, years }, i) => {
        const x = X0 + elapsed * SCALE;
        const w = years * SCALE;
        elapsed += years;
        const cx = x + w / 2;
        const above = i % 2 === 0;
        return (
          <g key={body}>
            <rect
              x={x}
              y={BAR_TOP}
              width={w}
              height={BAR_H}
              fill={above ? 'var(--mode-soft)' : 'var(--card)'}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            <line
              x1={cx}
              y1={above ? 48 : BAR_TOP + BAR_H + 2}
              x2={cx}
              y2={above ? BAR_TOP - 2 : 96}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            <text
              x={cx}
              y={above ? 34 : 108}
              textAnchor="middle"
              fontSize="9"
              fontWeight="600"
              fill="var(--text)"
            >
              {GRAHA_NAMES[body]}
            </text>
            <text
              x={cx}
              y={above ? 45 : 119}
              textAnchor="middle"
              fontSize="8"
              fill="var(--text-faint)"
            >
              {t('learn.fig.dashaBar.years', { n: years })}
            </text>
          </g>
        );
      })}

      <text x={180} y={142} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.dashaBar.note')}
      </text>
    </svg>
  );
}
